
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, type EmailSettings } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import Sidebar from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import Header from "@/components/Header";
import { useHeaderData } from "@/hooks/use-header-data";
import { Check, Loader2 } from "lucide-react";

const SettingsPage = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    emailProvider: "gmail",
    email: "",
    password: "",
    host: "",
    port: "",
    defaultRecipients: ""
  });
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  
  const { organization, organizations } = useHeaderData();

  useEffect(() => {
    const getSessionAndSettings = async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate("/login");
          return;
        }

        setUser(session.user);
        
        // Fetch email settings from the organization table
        if (organizationId) {
          const { data: org, error: orgError } = await supabase
            .from("organizations")
            .select("*")
            .eq("id", organizationId)
            .single();

          if (!orgError && org && org.email_settings) {
            // Parse email_settings JSON from the organization
            const settings = typeof org.email_settings === 'string' 
              ? JSON.parse(org.email_settings) 
              : org.email_settings;
              
            setEmailSettings({
              emailProvider: settings.emailProvider || "gmail",
              email: settings.email || "",
              password: settings.password || "",
              host: settings.host || "",
              port: settings.port || "",
              defaultRecipients: settings.defaultRecipients || ""
            });
          }
        }

      } catch (error: any) {
        console.error(error);
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    getSessionAndSettings();
  }, [navigate, toast, organizationId]);

  const updateEmailSettings = async () => {
    if (!organizationId) return;
    
    try {
      setSaving(true);
      
      const { error } = await supabase.from("organizations").update({
        email_settings: {
          emailProvider: emailSettings.emailProvider,
          email: emailSettings.email,
          password: emailSettings.password,
          host: emailSettings.host,
          port: emailSettings.port,
          defaultRecipients: emailSettings.defaultRecipients,
        }
      }).eq("id", organizationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Email settings updated successfully",
      });
    } catch (error: any) {
      console.error(error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailRecipient || !organizationId) {
      toast({
        title: "Error",
        description: "Please enter a recipient email address",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSendingTestEmail(true);
      setTestEmailSent(false);
      
      const response = await fetch('https://ejklusxvfzsqhpuvvmxp.functions.supabase.co/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient: testEmailRecipient,
          organization: organization?.name || 'Your Organization',
          settings: {
            emailProvider: emailSettings.emailProvider,
            email: emailSettings.email,
            password: emailSettings.password,
            host: emailSettings.host,
            port: emailSettings.port,
          }
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) throw new Error(result.message || 'Failed to send test email');
      
      setTestEmailSent(true);
      toast({
        title: "Success",
        description: "Test email sent successfully",
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to send test email',
        variant: "destructive",
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />
      
      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar className="border-r border-border/50" />
        
        <main className={cn(
          "flex-1 container mx-auto px-4 py-8",
          "relative z-10 bg-background"
        )}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Organization Settings</h1>
              <p className="text-muted-foreground">
                Configure organization-wide settings for {organization?.name || 'your organization'}.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Email Configuration</CardTitle>
                <CardDescription>
                  Configure email settings for notifications and reports
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="emailProvider">Email Provider</Label>
                    <select
                      id="emailProvider"
                      className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                      value={emailSettings.emailProvider}
                      onChange={(e) =>
                        setEmailSettings({
                          ...emailSettings,
                          emailProvider: e.target.value as "gmail" | "office365" | "smtp",
                        })
                      }
                    >
                      <option value="gmail">Gmail</option>
                      <option value="office365">Office 365</option>
                      <option value="smtp">Custom SMTP</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Email Address"
                      value={emailSettings.email}
                      onChange={(e) =>
                        setEmailSettings({
                          ...emailSettings,
                          email: e.target.value,
                        })
                      }
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="App Password or Account Password"
                      value={emailSettings.password}
                      onChange={(e) =>
                        setEmailSettings({
                          ...emailSettings,
                          password: e.target.value,
                        })
                      }
                    />
                  </div>

                  {emailSettings.emailProvider === 'smtp' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="host">SMTP Host</Label>
                        <Input
                          id="host"
                          placeholder="smtp.example.com"
                          value={emailSettings.host}
                          onChange={(e) =>
                            setEmailSettings({
                              ...emailSettings,
                              host: e.target.value,
                            })
                          }
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="port">SMTP Port</Label>
                        <Input
                          id="port"
                          placeholder="587"
                          value={emailSettings.port}
                          onChange={(e) =>
                            setEmailSettings({
                              ...emailSettings,
                              port: e.target.value,
                            })
                          }
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="defaultRecipients">Default Recipients (comma separated)</Label>
                    <Input
                      id="defaultRecipients"
                      placeholder="john@example.com, jane@example.com"
                      value={emailSettings.defaultRecipients}
                      onChange={(e) =>
                        setEmailSettings({
                          ...emailSettings,
                          defaultRecipients: e.target.value,
                        })
                      }
                    />
                  </div>
                  
                  <Button onClick={updateEmailSettings} disabled={saving} className="mt-4">
                    {saving ? "Saving..." : "Save Email Settings"}
                  </Button>
                </div>
                
                <div className="border-t pt-4 mt-6">
                  <CardTitle className="text-lg mb-4">Test Email Configuration</CardTitle>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="testEmail">Send test email to</Label>
                      <div className="flex gap-2">
                        <Input
                          id="testEmail"
                          type="email"
                          placeholder="recipient@example.com"
                          value={testEmailRecipient}
                          onChange={(e) => setTestEmailRecipient(e.target.value)}
                          className="flex-1"
                        />
                        <Button 
                          onClick={sendTestEmail} 
                          disabled={sendingTestEmail || !emailSettings.email || !testEmailRecipient}
                          className="whitespace-nowrap"
                        >
                          {sendingTestEmail ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : testEmailSent ? (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              Sent
                            </>
                          ) : (
                            "Send Test Email"
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
